/**
 * @fileoverview Hero Section - Homepage hero with background image
 *
 * Purpose: Primary hero section with headline, tagline, and CTA
 * Features:
 * - Full-screen hero with background image
 * - Responsive typography
 * - CTA button to open demo request dialog
 *
 * Used by: Homepage (index.tsx)
 */

import { useState } from "react";
import { Button } from "@/ui/primitives";
import { ArrowRight } from "lucide-react";
import { DemoRequestDialog } from "./-ui.demo-request-dialog";
import { usePostHog } from "@/utils/posthog";

/**
 * Hero section component
 *
 * Features a demo request button that opens a modal form.
 */
export function HeroSection() {
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);
  const posthog = usePostHog();

  const handleDemoRequest = () => {
    posthog?.capture("demo_requested", {
      button_location: "hero_section",
      button_text: "Book a demo",
    });
    setIsDemoDialogOpen(true);
  };

  return (
    <>
      <div className="relative -mt-[65px] flex min-h-[80vh] items-center overflow-hidden">
        {/* Background Image - full hero section.
            Mobile uses a clean background to avoid the busy swirl; desktop keeps the full image. */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/Homepage/footer_image.png"
            alt=""
            className="h-full w-full object-cover md:hidden"
          />
          <img
            src="/images/Homepage/header_image.png"
            alt=""
            className="hidden h-full w-full object-cover md:block"
          />
        </div>

        {/* Content */}
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-12 pt-[calc(3rem+65px)] md:py-16 md:pt-[calc(3.5rem+65px)]">
          <div className="flex items-center">
            <div className="max-w-2xl animate-hero-enter">
              <h1
                className="mb-6 text-4xl leading-snug md:whitespace-nowrap md:text-6xl md:leading-tight lg:text-[54px]"
                style={{
                  color: "#FCFAF2",
                  textShadow: "0 4px 18px rgba(0, 0, 0, 0.25)",
                }}
              >
                All-in-one AI for <br />
                insolvency &amp; restructuring
              </h1>
              <p
                className="mb-8 text-lg leading-relaxed"
                style={{ color: "#FCFAF2" }}
              >
                Morgan does the heavy lifting on every matter, from messy
                records <br className="hidden md:inline" />
                to clear analysis, so your team spends less time on write-offs{" "}
                <br className="hidden md:inline" />
                &amp; more time on recoveries &amp; claims.
              </p>
              <Button
                variant="secondary"
                size="lg"
                expandIcon
                className="gap-2 bg-[#FCFAF2] text-base text-black hover:bg-[#FCFAF2]/90"
                onClick={handleDemoRequest}
              >
                Book a demo
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <DemoRequestDialog
        open={isDemoDialogOpen}
        onOpenChange={setIsDemoDialogOpen}
      />
    </>
  );
}
